import {
  PREPROD_ANALYTICS_URL,
  PROD_ANALYTICS_URL,
} from "../../../common/Constants";
import { ENV } from "../../../utils/EmpUtil";
import { getAnalyticsTaggingContext } from "../../../utils/marketBrandUtils";
import {
  customerHandlingPageLoadDetailDataLayer,
  onboardingOnClickDataLayer,
  onClickDataLayer,
  onClickDetailDataLayer,
  onClickDetailDataLayerWithPaymentType,
  pageLoadDataLayer,
  pageLoadDetailDataLayer,
  referralExitDataLayer,
  internalReferralExitDataLayer,
} from "./DataLayer";

let analyticsLoaded;
let deviceType;
let trackedPages = new Set();

const BRAND_PREFIX_PATTERN = /^(?:fv|foc|ln|lnc):/;

const getTaggedPageName = (pageName, prefix) => {
  if (typeof pageName !== "string") {
    return pageName;
  }

  return `${prefix}:${pageName.replace(BRAND_PREFIX_PATTERN, "")}`;
};

// Link names are only retagged when they already carry a brand prefix;
// values such as "referral:exit" or "emp:..." must pass through unchanged.
const getTaggedLinkName = (linkName, prefix) => {
  if (typeof linkName !== "string" || !BRAND_PREFIX_PATTERN.test(linkName)) {
    return linkName;
  }

  return getTaggedPageName(linkName, prefix);
};

const checkDeviceType = () => {
  let radUiString = "";
  const deviceOptions = {
    pc: "ui:rad:pc",
    tablet: "ui:rad:tablet",
    mobile: "ui:rad:mobile",
  };

  // Use a more comprehensive check for mobile devices
  const userAgent = navigator.userAgent.toLowerCase();

  if (
    /iphone|android|phone|mobile|iemobile|opera mini|blackberry|fennec|iemobile|opera mobi|windows phone/i.test(
      userAgent
    )
  ) {
    radUiString = deviceOptions["mobile"];
  } else if (/ipad|tablet|playbook|silk|android(?!.*mobile)/i.test(userAgent)) {
    radUiString = deviceOptions["tablet"];
  } else {
    radUiString = deviceOptions["pc"];
  }

  deviceType = radUiString;
};

const generateTimestamp = (dateStr) => {
  return new Date(dateStr).toISOString();
};

const initAnalytics = () => {
  if (!globalThis.__satelliteLoaded) {
    const analyticsUrl =
      ENV !== "prod" ? PREPROD_ANALYTICS_URL : PROD_ANALYTICS_URL;

    // Add preconnect
    const preconnectLink = document.createElement("link");
    preconnectLink.rel = "preconnect";
    preconnectLink.href = new URL(analyticsUrl).origin;
    document.head.appendChild(preconnectLink);

    const script = document.createElement("script");
    script.src = analyticsUrl;
    script.async = true;
    document.head.appendChild(script);
    checkDeviceType();
  }
};

const trackPageLoadEvent = (pageName, user, siteSection) => {
  const trackingKey = `${pageName}-${Date.now()}`;
  const recentTracking = Array.from(trackedPages).find(
    (key) =>
      key.startsWith(pageName) &&
      Date.now() - parseInt(key.split("-")[1]) < 1000
  );

  if (recentTracking) {
    return;
  }

  trackedPages.add(trackingKey);

  setTimeout(() => {
    trackedPages.delete(trackingKey);
  }, 2000);

  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const timeStampPL = generateTimestamp(new Date());

  if (globalThis.__satelliteLoaded) {
    checkDeviceType();
    globalThis.digitaldata = pageLoadDataLayer(
      taggedPageName,
      dealerId,
      deviceType,
      timeStampPL,
      siteSection,
      client,
      site
    );
    globalThis._satellite.track("empLoad");
    return;
  }

  globalThis.digitaldata = pageLoadDataLayer(
    taggedPageName,
    dealerId,
    deviceType,
    timeStampPL,
    siteSection,
    client,
    site
  );
  let num = 0;

  const eventInterval = setInterval(() => {
    num += 1;

    if (globalThis.__satelliteLoaded) {
      analyticsLoaded = globalThis.__satelliteLoaded;
      checkDeviceType();
      // page components are responsible for passing the correct payload
      globalThis.digitaldata = pageLoadDataLayer(
        taggedPageName,
        dealerId,
        deviceType,
        timeStampPL,
        siteSection,
        client,
        site
      );
      if (analyticsLoaded) {
        globalThis._satellite.track("empLoad");
      }
      clearInterval(eventInterval);
    }

    if (num === 10) {
      clearInterval(eventInterval);
    }
  }, 1000);
};

const trackPageLoadDetailEvent = (pageName, tool, orderId, user) => {
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const timeStampPL = generateTimestamp(new Date());

  if (globalThis.__satelliteLoaded) {
    checkDeviceType();
    globalThis.digitaldata = pageLoadDetailDataLayer(
      taggedPageName,
      tool,
      orderId,
      dealerId,
      deviceType,
      timeStampPL,
      client,
      site
    );
    globalThis._satellite.track("empLoadDetail");
    return;
  }

  globalThis.digitaldata = pageLoadDetailDataLayer(
    taggedPageName,
    tool,
    orderId,
    dealerId,
    deviceType,
    timeStampPL,
    client,
    site
  );
  let num = 0;

  const eventInterval = setInterval(() => {
    num += 1;
    if (globalThis.__satelliteLoaded) {
      analyticsLoaded = globalThis.__satelliteLoaded;
      checkDeviceType();
      // page components are responsible for passing the correct payload
      globalThis.digitaldata = pageLoadDetailDataLayer(
        taggedPageName,
        tool,
        orderId,
        dealerId,
        deviceType,
        timeStampPL,
        client,
        site
      );
      if (analyticsLoaded) {
        globalThis._satellite.track("empLoadDetail");
      }
      clearInterval(eventInterval);
    }
    if (num === 10) {
      clearInterval(eventInterval);
    }
  }, 100);
};

const trackCustomerHandlingPageLoadDetailEvent = (
  pageName,
  pageNameNoVehicle,
  tool,
  toolDescriptor,
  user,
  modelYear,
  nameplate
) => {
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const taggedPageNameNoVehicle = getTaggedPageName(pageNameNoVehicle, prefix);
  const timeStampPL = generateTimestamp(new Date());

  if (globalThis.__satelliteLoaded) {
    checkDeviceType();
    globalThis.digitaldata = customerHandlingPageLoadDetailDataLayer(
      taggedPageName,
      taggedPageNameNoVehicle,
      tool,
      toolDescriptor,
      dealerId,
      deviceType,
      timeStampPL,
      modelYear,
      nameplate,
      client,
      site
    );
    globalThis._satellite.track("empLoadDetail");
    return;
  }

  globalThis.digitaldata = customerHandlingPageLoadDetailDataLayer(
    taggedPageName,
    taggedPageNameNoVehicle,
    tool,
    toolDescriptor,
    dealerId,
    deviceType,
    timeStampPL,
    modelYear,
    nameplate,
    client,
    site
  );
  let num = 0;

  const eventInterval = setInterval(() => {
    num += 1;
    if (globalThis.__satelliteLoaded) {
      analyticsLoaded = globalThis.__satelliteLoaded;
      checkDeviceType();
      // page components are responsible for passing the correct payload
      globalThis.digitaldata = customerHandlingPageLoadDetailDataLayer(
        taggedPageName,
        taggedPageNameNoVehicle,
        tool,
        toolDescriptor,
        dealerId,
        deviceType,
        timeStampPL,
        modelYear,
        nameplate,
        client,
        site
      );
      if (analyticsLoaded) {
        globalThis._satellite.track("empLoadDetail");
      }
      clearInterval(eventInterval);
    }
    if (num === 10) {
      clearInterval(eventInterval);
    }
  }, 100);
};

const trackOnClickEvent = (
  pageName,
  onclick,
  onclickLinkName,
  user,
  siteSection
) => {
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const taggedLinkName = getTaggedLinkName(onclickLinkName, prefix);
  const timeStampCTA = generateTimestamp(new Date());

  globalThis.digitaldata = onClickDataLayer(
    taggedPageName,
    onclick,
    taggedLinkName,
    dealerId,
    deviceType,
    timeStampCTA,
    siteSection,
    client,
    site
  );

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("empOnclick");
  }
};

const trackOnClickDetailEvent = (...args) => {
  const [
    pageName,
    tool,
    toolDescriptor,
    orderId,
    onclick,
    onclickLinkName,
    user,
    paymentType,
    modelYear,
    nameplate,
  ] = args;
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const taggedLinkName = getTaggedLinkName(onclickLinkName, prefix);
  const timeStampCTA = generateTimestamp(new Date());
  checkDeviceType();

  if (paymentType) {
    globalThis.digitaldata = onClickDetailDataLayerWithPaymentType(
      taggedPageName,
      tool,
      toolDescriptor,
      orderId,
      onclick,
      taggedLinkName,
      dealerId,
      deviceType,
      timeStampCTA,
      paymentType,
      modelYear,
      nameplate,
      client,
      site
    );
  } else {
    globalThis.digitaldata = onClickDetailDataLayer(
      taggedPageName,
      tool,
      toolDescriptor,
      orderId,
      onclick,
      taggedLinkName,
      dealerId,
      deviceType,
      timeStampCTA,
      modelYear,
      nameplate,
      client,
      site
    );
  }

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("empOnclickDetail");
  }
};

const trackOnboardingOnClickEvent = (
  pageName,
  onclick,
  onclickLinkName,
  user,
  eventAction
) => {
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const taggedLinkName = getTaggedLinkName(onclickLinkName, prefix);
  const timeStampCTA = generateTimestamp(new Date());
  checkDeviceType();

  globalThis.digitaldata = onboardingOnClickDataLayer(
    taggedPageName,
    onclick,
    taggedLinkName,
    dealerId,
    deviceType,
    timeStampCTA,
    eventAction,
    client,
    site
  );

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("empOnclickOnboarding");
  }
};

const trackOnDownloadEvent = (pageName, onclick, onclickLinkName, user) => {
  const { dealerId, brand, market } = user;
  const { prefix, client, site } = getAnalyticsTaggingContext(market, brand);
  const taggedPageName = getTaggedPageName(pageName, prefix);
  const taggedLinkName = getTaggedLinkName(onclickLinkName, prefix);
  const timeStampCTA = generateTimestamp(new Date());

  globalThis.digitaldata = onClickDataLayer(
    taggedPageName,
    onclick,
    taggedLinkName,
    dealerId,
    deviceType,
    timeStampCTA,
    undefined,
    client,
    site
  );

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("empDownload");
  }
};

const trackReferralExit = (exitDomain, orderId, user, siteSection) => {
  const { dealerId, brand, market } = user;
  const { client, site } = getAnalyticsTaggingContext(market, brand);
  const timeStampCTA = generateTimestamp(new Date());
  const parsedUrl = new URL(exitDomain);
  const hostname = parsedUrl.hostname;

  globalThis.digitaldata = referralExitDataLayer(
    orderId,
    `referral:${hostname}`,
    `referral:exit`,
    dealerId,
    deviceType,
    timeStampCTA,
    siteSection,
    client,
    site
  );

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("referral-exit");
  }
};

const trackInternalReferralExit = (
  exitDomain,
  user,
  siteSection,
  referredTo
) => {
  const { dealerId, brand, market } = user;
  const { client, site } = getAnalyticsTaggingContext(market, brand);
  const timeStampCTA = generateTimestamp(new Date());
  const parsedUrl = new URL(exitDomain);
  const hostname = parsedUrl.hostname;
  checkDeviceType();

  globalThis.digitaldata = internalReferralExitDataLayer(
    `referral:${hostname}`,
    `referral:exit`,
    dealerId,
    deviceType,
    timeStampCTA,
    referredTo,
    siteSection,
    client,
    site
  );

  if (globalThis.__satelliteLoaded) {
    globalThis._satellite.track("referral-internal-exit");
  }
};

export {
  initAnalytics,
  trackPageLoadEvent,
  trackPageLoadDetailEvent,
  trackCustomerHandlingPageLoadDetailEvent,
  trackOnClickEvent,
  trackOnClickDetailEvent,
  trackOnDownloadEvent,
  trackReferralExit,
  trackOnboardingOnClickEvent,
  trackInternalReferralExit,
};
